import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronDown, Phone, MessageCircle, ArrowRight } from 'lucide-react';
import { companyInfo } from '../data/mock';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '../components/ui/accordion';

const FAQPage = () => {
  const [activeCategory, setActiveCategory] = useState('General');

  const faqCategories = {
    'General': [
      {
        q: "What does Construction Cards Helpline Limited do?",
        a: "Construction Cards Helpline Limited (Company No. 13678504) is a UK-based service provider specialising in construction certification support. We help construction workers across the UK obtain CSCS cards, book CSCS tests, complete NVQ qualifications (Level 2-7), and access CPCS and NPORS training courses. We act as an intermediary, guiding you through the application process and arranging bookings with accredited providers."
      },
      {
        q: "Are you an official CSCS card provider?",
        a: "We are an authorised service provider that assists with CSCS card applications and test bookings. While we are not the issuing authority (CSCS is managed by CITB), we work directly with accredited test centres and awarding bodies to facilitate your application. We provide expert guidance, handle bookings, and ensure your application meets all requirements."
      },
      {
        q: "How long has your company been established?",
        a: "Construction Cards Helpline Limited was established on 14th October 2021 and is registered in the United Kingdom with Company Number 13678504. Since our establishment, we have helped thousands of construction workers obtain their qualifications and get back on site quickly."
      },
      {
        q: "Do you operate nationwide?",
        a: "Yes, we provide services across the entire United Kingdom. Whether you're in London, Manchester, Birmingham, Glasgow, or anywhere else in the UK, we can arrange your CSCS test bookings, NVQ assessments, and training courses at centres convenient to your location."
      },
      {
        q: "How can I contact you?",
        a: "You can reach us by phone at +44 7848 482492, via email at info@constructioncardshelpline.co.uk, or through WhatsApp at +44 7848 482492. Our team is available Monday to Friday, 8:00 AM - 6:00 PM, and Saturday, 9:00 AM - 2:00 PM."
      }
    ],
    'CSCS Cards': [
      {
        q: "What is a CSCS card?",
        a: "A CSCS (Construction Skills Certification Scheme) card is the UK's leading health and safety certification scheme for the construction industry. It proves that you have the required training and qualifications for your role on a construction site. Most UK construction sites require all workers to hold a valid CSCS card before being allowed on site."
      },
      {
        q: "Which CSCS card do I need?",
        a: "The type of CSCS card you need depends on your role and qualifications:\n\n• Green Labourer Card: For construction labourers (requires Level 1 Award + CSCS test)\n• Blue Skilled Worker Card: For skilled tradespeople (requires NVQ Level 2 + operative test)\n• Gold Card: For advanced/supervisory roles (requires NVQ Level 3 or 4)\n• Black Card: For managers (requires NVQ Level 6 or 7 + SMSTS)\n\nNot sure which card you need? Contact our team for free guidance - we'll help you choose the right card for your role."
      },
      {
        q: "How do I apply for a CSCS card?",
        a: "Applying for a CSCS card through Construction Cards Helpline is simple:\n\n1. Contact us via phone, email, or our online form\n2. We'll assess your needs and advise on the right card type\n3. We'll arrange your CSCS test booking or Level 1 Award course\n4. Complete your test/course (we provide preparation materials)\n5. Once you pass, we'll assist with your card application\n6. Your CSCS card will be sent directly to you\n\nThe entire process typically takes 1-3 weeks depending on the card type."
      },
      {
        q: "How long does it take to receive my CSCS card?",
        a: "Once you've passed your test and submitted your application, CSCS typically processes cards within 7-10 working days. For Green Labourer cards with our fast-track Level 1 Award course, you can often complete the entire process within 1-2 weeks. Skilled worker cards (Blue, Gold, Black) require NVQ completion first, which takes 6-16 weeks depending on the level."
      },
      {
        q: "What happens if I fail the CSCS test?",
        a: "If you don't pass your CSCS test on the first attempt, don't worry - you can retake it. We provide comprehensive test preparation materials to improve your chances of passing. You'll need to book a new test date (we can help arrange this) and pay the test centre fee again. Most people pass on their second attempt with proper preparation."
      },
      {
        q: "Can you help me choose the right card?",
        a: "Absolutely! Choosing the right CSCS card is crucial. Our expert team will discuss your:\n\n• Current role and trade\n• Existing qualifications\n• Career goals\n• Site requirements\n\nWe'll then recommend the most appropriate card and guide you through the entire application process. This service is completely free - call us today on +44 7848 482492."
      },
      {
        q: "Is the Green Labour Card suitable for beginners?",
        a: "Yes, the Green CSCS Labourer Card is perfect for beginners and those new to construction. It's the entry-level card designed for construction labourers and requires no prior qualifications. You only need to complete a 1-day Level 1 Award in Health and Safety course and pass the CSCS touchscreen test. Best of all, the Green Labour Card is valid for your lifetime - no renewal needed!"
      },
      {
        q: "How much does a CSCS card cost?",
        a: "CSCS card costs vary by type:\n\n• Green Labourer Card: From £150 (includes Level 1 Award course + test)\n• Blue Skilled Worker Card: From £1,200 (includes NVQ Level 2 + test)\n• Gold Card: From £1,800 (includes NVQ Level 3/4 + test)\n• Black Card: From £2,500 (includes NVQ Level 6/7 + test)\n\nPrices include our service fee, course/test costs, and application support. Contact us for an exact quote based on your specific needs."
      }
    ],
    'CSCS Test': [
      {
        q: "How do I book a CSCS test?",
        a: "Booking a CSCS test through us is easy. Simply contact us via phone (+44 7848 482492), email, or WhatsApp, and we'll:\n\n1. Find your nearest test centre\n2. Check available dates\n3. Book your preferred slot\n4. Send you confirmation and preparation materials\n5. Remind you before your test date\n\nWe handle all the booking administration, so you can focus on preparing for your test."
      },
      {
        q: "What ID do I need for the CSCS test?",
        a: "You must bring one form of valid photographic identification to your CSCS test:\n\n• UK/EU Passport\n• UK Driving Licence (photocard)\n• National Identity Card\n• Biometric Residence Permit\n\nThe ID must be in date and show your photo, name, and date of birth. Without valid ID, you will be refused entry and your test fee will not be refunded. We'll remind you about ID requirements before your test date."
      },
      {
        q: "How long is the CSCS test?",
        a: "The CSCS touchscreen test lasts 45 minutes and consists of 50 multiple-choice questions. You need to score at least 45 out of 50 (90%) to pass. The test is available in over 40 languages. Results are provided immediately after you complete the test."
      },
      {
        q: "Can I reschedule my CSCS test?",
        a: "Yes, you can reschedule your test, but it must be done at least 3 working days before your booked date. Contact us as soon as possible if you need to change your test date, and we'll rearrange it for you. Rescheduling within 3 days of the test or failing to attend will result in losing your test fee."
      },
      {
        q: "Do you provide CSCS test preparation materials?",
        a: "Yes! When you book your CSCS test through us, we provide free access to:\n\n• Practice test questions\n• Health and safety study guides\n• Mock tests\n• Test tips and strategies\n\nThese materials significantly improve your chances of passing first time. We want you to succeed!"
      }
    ],
    'NVQ Qualifications': [
      {
        q: "What is an NVQ?",
        a: "NVQ (National Vocational Qualification) is a work-based qualification that assesses your practical skills and knowledge in your trade. Unlike classroom courses, NVQs are assessed based on your real work performance. They're essential for obtaining Blue, Gold, and Black CSCS cards and are highly valued by UK construction employers."
      },
      {
        q: "Which NVQ level do I need?",
        a: "NVQ levels correspond to different CSCS cards:\n\n• Level 2: Blue Skilled Worker Card (tradespeople - electricians, plumbers, bricklayers, etc.)\n• Level 3: Gold Card (advanced craft/supervisory roles)\n• Level 4: Gold Card (advanced supervisory/management)\n• Level 6: Black Card (construction site management)\n• Level 7: Black Card (senior construction management)\n\nYour required level depends on your current role, experience, and career goals. We'll assess your situation and recommend the appropriate level."
      },
      {
        q: "How long does an NVQ take to complete?",
        a: "NVQ completion times vary:\n\n• Level 2: 6-8 weeks\n• Level 3: 8-12 weeks\n• Level 4: 10-14 weeks\n• Level 6: 12-16 weeks\n• Level 7: 16-20 weeks\n\nThese are typical timeframes, but actual duration depends on your evidence availability, assessor schedules, and how quickly you can demonstrate competence. We offer fast-track options where possible."
      },
      {
        q: "Do I need to attend classes for an NVQ?",
        a: "No, NVQs are work-based qualifications, not classroom courses. You don't need to attend classes or take exams. Instead:\n\n• You continue working in your normal job\n• You build a portfolio of evidence (photos, videos, documents)\n• An assessor visits you on site to observe your work\n• Your competence is assessed against industry standards\n\nThis makes NVQs convenient for working professionals. We provide remote support throughout the process."
      },
      {
        q: "Is there an on-site assessment?",
        a: "Yes, NVQ qualifications include on-site assessments where a qualified assessor visits your workplace to observe you performing your trade. These visits are arranged at times convenient to you and your employer. The assessor will verify that you can competently perform tasks required for your qualification level. We coordinate all assessor visits to minimize disruption to your work."
      },
      {
        q: "Is NVQ completion guaranteed?",
        a: "While we provide comprehensive support and guidance, NVQ completion depends on:\n\n• Your competence in your trade\n• Quality and quantity of evidence you provide\n• Meeting awarding body standards\n• Assessor availability\n\nWe cannot guarantee qualification outcomes as final decisions rest with independent assessors and awarding bodies. However, our experienced team will support you throughout to maximize your chances of success. Our pass rate is 95%."
      }
    ],
    'CPCS & NPORS': [
      {
        q: "What is the difference between CPCS and NPORS?",
        a: "Both are plant operator certification schemes, but with key differences:\n\n**CPCS (Construction Plant Competence Scheme):**\n• Industry-standard, widely recognized\n• Required by most major construction sites\n• Red card (2 years) then Blue card (5 years)\n• Requires NVQ for Blue card upgrade\n\n**NPORS (National Plant Operators Registration Scheme):**\n• Recognized alternative to CPCS\n• More flexible, often faster to obtain\n• Accepted on many sites\n• May not be accepted on all major projects\n\nWe recommend CPCS for maximum site access, but NPORS can be suitable depending on your work. We offer both."
      },
      {
        q: "Which plant operator course do I need?",
        a: "This depends on the machinery you'll be operating. Popular courses include:\n\n• 360 Excavator (A59) - tracked and wheeled\n• Forward Tipping Dumper (A09)\n• Ride on Roller (A31)\n• Telescopic Handler (A17) - with/without 360 slew\n• Crawler Crane\n• Tower Crane\n\nNot sure which course you need? Tell us what machines you'll be operating, and we'll recommend the correct CPCS or NPORS category."
      },
      {
        q: "Do I need previous experience for CPCS courses?",
        a: "For most CPCS categories, some prior experience is recommended but not always required. Training courses include:\n\n• Novice courses (3-5 days): For those with little to no experience\n• Experienced worker tests (1-2 days): For those with existing skills\n\nWe'll assess your experience level and recommend the appropriate course format. Even beginners can become certified with proper training."
      },
      {
        q: "How long are CPCS/NPORS courses?",
        a: "Course duration varies by machinery type and your experience:\n\n• 360 Excavator: 5 days training + 1 day test\n• Forward Tipping Dumper: 3 days training + 1 day test\n• Ride on Roller: 3 days training + 1 day test\n• Telescopic Handler: 5 days training + 1 day test\n\nExperienced workers may qualify for shorter 'test-only' routes. We'll match you with the right course length for your skill level."
      },
      {
        q: "Are CPCS and NPORS cards nationally recognised?",
        a: "Yes, both schemes are nationally recognised across the UK construction industry. CPCS is the industry standard and accepted on virtually all UK sites. NPORS is widely accepted, though some major contractors may specify CPCS. If you're unsure which scheme your employer or site requires, check with them first - or contact us for advice."
      }
    ],
    'Payments & Bookings': [
      {
        q: "When do I need to pay?",
        a: "Payment is typically required at the time of booking to secure your test date or course place. For NVQ qualifications, we may offer payment plans - contact us to discuss options. We accept bank transfer, credit/debit cards, and can arrange installment payments for larger course fees."
      },
      {
        q: "Are payments refundable?",
        a: "Our refund policy depends on timing:\n\n• 14+ days before your test/course: Full refund minus £50 admin fee\n• 7-14 days before: 50% refund\n• Less than 7 days: No refund\n• Missed appointments: No refund\n• Test failure: No refund (retake fees apply)\n\nThird-party fees (test centre charges, awarding body fees) are non-refundable in all cases. See our full Terms & Conditions for details."
      },
      {
        q: "What happens if I miss my test or course?",
        a: "If you miss your scheduled test or course without prior notice, you will forfeit your booking fee with no refund. We strongly recommend:\n\n• Setting calendar reminders\n• Arriving 15 minutes early\n• Contacting us immediately if you have an emergency\n\nIf you contact us at least 3 working days before your appointment, we can reschedule for a small admin fee (subject to availability)."
      },
      {
        q: "Can I change my booking?",
        a: "Yes, you can change your booking if you give us at least 3 working days' notice. Contact us by phone or email, and we'll rearrange your test or course date subject to availability. Changes requested with less than 3 days' notice may incur a rescheduling fee or may not be possible."
      },
      {
        q: "Do you offer payment plans?",
        a: "For NVQ courses and some higher-value training packages, we may be able to arrange payment plans. Contact us to discuss your specific situation, and we'll work with you to find a payment structure that suits your budget. Terms vary depending on the course and individual circumstances."
      }
    ],
    'After Application': [
      {
        q: "How will I receive confirmation?",
        a: "After booking, you'll receive:\n\n• Immediate email confirmation with booking details\n• Test/course location and date\n• What to bring (ID requirements)\n• Preparation materials (where applicable)\n• Reminder email 3-5 days before your appointment\n\nMake sure to check your spam folder and add our email address to your contacts. If you don't receive confirmation within 24 hours, contact us immediately."
      },
      {
        q: "How long does card processing take?",
        a: "After successfully completing your test or qualification:\n\n• CSCS Green Card: 7-10 working days\n• CSCS Blue/Gold/Black Cards: 7-10 working days after NVQ completion\n• CPCS Red Card: 2-3 weeks\n• CPCS Blue Card: Requires NVQ (6-16 weeks) + card processing (2-3 weeks)\n\nCards are sent by post to your registered address. We'll keep you updated throughout the process."
      },
      {
        q: "Who do I contact for support after applying?",
        a: "Our support team is here to help throughout your journey. Contact us for:\n\n• Booking queries: +44 7848 482492 or info@constructioncardshelpline.co.uk\n• Test preparation questions\n• Card status updates\n• Technical support\n• General advice\n\nWe're available Monday-Friday 8am-6pm, Saturday 9am-2pm. For urgent matters, use WhatsApp: +44 7848 482492."
      },
      {
        q: "What if my card doesn't arrive?",
        a: "If your CSCS or CPCS card hasn't arrived within the expected timeframe:\n\n1. Check with family/housemates if it was delivered\n2. Verify your address on your application was correct\n3. Contact us - we'll check the status with the issuing body\n4. If lost in post, we'll help arrange a replacement\n\nReplacement cards may incur a small fee from the issuing body. Always keep your booking reference number handy when contacting us."
      }
    ]
  };

  const categories = Object.keys(faqCategories);

  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="bg-construction-darkblue text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">
              Frequently Asked Questions
            </h1>
            <p className="text-xl text-gray-200 mb-6 leading-relaxed">
              Get answers to common questions about CSCS cards, NVQ qualifications, CPCS training, and our services
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <a
                href={`tel:${companyInfo.phone}`}
                className="inline-flex items-center gap-2 bg-construction-yellow text-construction-darkblue px-6 py-3 rounded-lg font-bold hover:bg-yellow-500 transition-all"
              >
                <Phone size={20} />
                Call Us
              </a>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 bg-white text-construction-darkblue px-6 py-3 rounded-lg font-bold hover:bg-gray-100 transition-all"
              >
                Contact Form
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            {/* Category Tabs */}
            <div className="mb-12">
              <div className="flex flex-wrap gap-3 justify-center">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => setActiveCategory(category)}
                    className={`px-6 py-3 rounded-lg font-semibold transition-all ${
                      activeCategory === category
                        ? 'bg-construction-yellow text-construction-darkblue shadow-md'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category}
                  </button>
                ))}
              </div>
            </div>

            {/* Questions */}
            {categories.map((category) => (
              activeCategory === category && (
                <div key={category} className="space-y-6">
                  <div className="bg-construction-yellow/10 border-l-4 border-construction-yellow p-6 rounded-r-lg mb-6">
                    <h2 className="text-2xl font-bold text-construction-darkblue mb-2">
                      {category}
                    </h2>
                    <p className="text-gray-700">
                      Common questions about {category.toLowerCase()} - scroll down to find your answer
                    </p>
                  </div>
                  
                  <Accordion type="single" collapsible className="space-y-4">
                    {faqCategories[category].map((item, index) => (
                      <AccordionItem 
                        key={index} 
                        value={`item-${index}`}
                        className="bg-gray-50 rounded-lg border-2 border-gray-200 hover:border-construction-yellow transition-colors px-6"
                      >
                        <AccordionTrigger className="text-left hover:no-underline py-5">
                          <span className="font-bold text-construction-darkblue pr-4 text-lg">
                            {item.q}
                          </span>
                        </AccordionTrigger>
                        <AccordionContent className="text-gray-700 leading-relaxed pb-5 text-base whitespace-pre-line">
                          {item.a}
                        </AccordionContent>
                      </AccordionItem>
                    ))}
                  </Accordion>

                  {/* In-section CTA */}
                  <div className="bg-construction-darkblue text-white rounded-xl p-6 text-center mt-8">
                    <p className="text-lg mb-4">
                      Still have questions about {category.toLowerCase()}?
                    </p>
                    <div className="flex flex-wrap gap-3 justify-center">
                      <a
                        href={`tel:${companyInfo.phone}`}
                        className="inline-flex items-center gap-2 bg-construction-yellow text-construction-darkblue px-6 py-3 rounded-lg font-bold hover:bg-yellow-500 transition-all"
                      >
                        <Phone size={20} />
                        Speak to an Advisor
                      </a>
                      <Link
                        to="/contact"
                        className="inline-flex items-center gap-2 bg-white text-construction-darkblue px-6 py-3 rounded-lg font-bold hover:bg-gray-100 transition-all"
                      >
                        Get in Touch
                      </Link>
                    </div>
                  </div>
                </div>
              )
            ))}
          </div>
        </div>
      </section>

      {/* SEO Keywords Section */}
      <section className="py-12 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl font-bold text-construction-darkblue mb-6 text-center">
              We Can Help With
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
              {[
                'CSCS Card UK',
                'CSCS Test Booking',
                'Green CSCS Card',
                'Blue CSCS Card',
                'NVQ for CSCS',
                'CPCS Course',
                'NPORS Training',
                'Construction Certification'
              ].map((keyword, index) => (
                <div key={index} className="bg-white p-4 rounded-lg shadow-sm border border-gray-200">
                  <p className="font-semibold text-construction-darkblue">{keyword}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-16 bg-construction-yellow">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-construction-darkblue mb-4">
            Ready to Get Qualified?
          </h2>
          <p className="text-xl text-construction-darkblue/80 mb-8 max-w-2xl mx-auto">
            Don't let confusion hold you back. Our expert team is here to guide you through every step of the process.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-2 bg-construction-darkblue text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-black transition-all hover:scale-105 shadow-lg"
            >
              Apply Now
              <ArrowRight size={20} />
            </Link>
            <a
              href={`tel:${companyInfo.phone}`}
              className="inline-flex items-center justify-center gap-2 bg-white text-construction-darkblue px-8 py-4 rounded-lg font-bold text-lg hover:bg-gray-100 transition-all hover:scale-105 shadow-lg"
            >
              <Phone size={24} />
              Call Our Team
            </a>
            <a
              href={`https://wa.me/${companyInfo.whatsapp.replace(/\+/g, '')}?text=Hi, I have a question about construction cards`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center justify-center gap-2 bg-green-600 text-white px-8 py-4 rounded-lg font-bold text-lg hover:bg-green-700 transition-all hover:scale-105 shadow-lg"
            >
              <MessageCircle size={24} />
              WhatsApp Us
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default FAQPage;
